// src/components/games/dungeon/dungeonState.ts

import { GameState, Room, Character, SaveData, ProgressInfo } from './dungeonTypes';
import { roomDescs } from './dungeonData';
import { saveGameToCloud, loadGameFromCloud, hasCloudSave } from '../../../services/oldschoolDungeonEscapeService';
import { logger } from '../../../utils/logger';

export function createInitialGameState(): GameState {
  return {
    tel: 0,
    mag: 0,
    trap: 0,
    gear: 0,
    mech: 0,
    cry: 0,
    lake: 0,
    fin: 0,
    garg: 0,
    ciph: 0,
    gcrow: 0,
    scrl: 3,
    braz: 0,
    puzz: 0,
    orb: 0,
    grid: [1, 2, 3, 4, 0, 5, 6, 7, 8]
  };
}

export function createInitialCharacter(): Character {
  return {
    inv: [],
    loc: "starting area"
  };
}

export function createInitialRunes(): string[] {
  return ["   ", " a ", " b ", " c ", " d ", " e ", " f ", " g ", " h "];
}

export function updateRoomDescriptions(
  dungeon: { [key: string]: Room },
  gs: GameState
): void {
  // Calculate progress to determine starting area desc
  const progress = calculateProgress(dungeon, gs);

  // CRITICAL: Update starting area description based on progress
  // After significant progress OR discovering trapdoor hold, hint at more secrets
  if (progress.pct >= 25 || dungeon["trapdoor hold"]?.v) {
    if (dungeon["starting area"].e.west) {
      // After lantern use - magical passage revealed
      dungeon["starting area"].ld = "the starting area. This is where you began your journey into the depths, and where ancient magic has revealed a shimmering passage to the west. The mystical symbols still pulse faintly along the stone walls, their ethereal light casting dancing shadows across the rocky floor.";
    } else {
      // Before lantern use but after progress - hint at secrets
      dungeon["starting area"].ld = "the starting area. This is where you started, but now the plain rock path seems suspiciously empty. Perhaps it may hold an additional secret?";
    }
  } else if (dungeon["starting area"].e.west && !dungeon["starting area"].ld.includes("shimmering passage")) {
    // If we just added the west passage but haven't hit 25% progress yet
    dungeon["starting area"].ld = "the starting area, the west end of a sloping east-west passage of barren rock. Ancient magic has revealed a shimmering passage to the west, where mystical symbols pulse with ethereal light along the stone walls.";
  }

  // Update attic room descriptions
  if (dungeon["attic room"].e.south) {
    // Window is broken
    if (dungeon["attic room"].c.includes("folding ladder")) {
      dungeon["attic room"].ld = "the attic, now disheveled and scattered with broken glass. The south-facing window that overlooks the roof in the moonlight is broken wide open. You notice an opening in the ceiling that might lead somewhere interesting - and now there's a sturdy folding ladder unfolded directly beneath it, providing easy access to whatever lies above.";
    } else {
      dungeon["attic room"].ld = "the attic, now disheveled and scattered with broken glass. The south-facing window that overlooks the roof in the moonlight is broken wide open. You notice an opening in the ceiling that might lead somewhere interesting, if only you could reach it.";
    }
  } else if (dungeon["attic room"].c.includes("folding ladder")) {
    dungeon["attic room"].ld = "the attic. It looks like it hasn't been entered in years. This place is filled with cobwebs, and a dirty south-facing window overlooks the roof in the moonlight, but is locked tight. You notice an opening in the ceiling that might lead somewhere interesting - and now there's a sturdy folding ladder unfolded directly beneath it, providing easy access to whatever lies above.";
  }

  // Update attic up exit
  if (dungeon["attic room"].c.includes("folding ladder")) {
    dungeon["attic room"].e.up = "observatory";
  } else {
    delete dungeon["attic room"].e.up;
  }

  // Update observatory - simpler description after star chart is taken
  if (!dungeon["observatory"].c.includes("incomplete star chart")) {
    dungeon["observatory"].ld = "a circular observatory with a great brass telescope pointing skyward through an open dome. A celestial globe sits on a marble pedestal, slowly rotating.";
  }

  // Update closet
  if (dungeon["secret"]?.v && !dungeon["closet"].e.down) {
    dungeon["closet"].e.down = "secret";
    if (dungeon["closet"].c.includes("folding ladder")) {
      dungeon["closet"].ld = "a musty, abandoned closet. There is an odd fog swirling on the floor, as if rolling in from the east. A folding wooden ladder leans against the back wall, looking sturdy despite its age. The secret hatch you discovered earlier remains open in the floor, providing easy access to the chamber below.";
    } else {
      dungeon["closet"].ld = "a musty, abandoned closet. There is an odd fog swirling on the floor, as if rolling in from the east. The secret hatch you discovered earlier remains open in the floor, providing easy access to the chamber below.";
    }
  } else if (dungeon["closet"].c.includes("folding ladder")) {
    // Ladder in closet but secret not discovered yet
    dungeon["closet"].ld = "a musty, abandoned closet. There is an odd fog swirling on the floor, as if rolling in from the east. A folding wooden ladder leans against the back wall, looking sturdy despite its age.";
  }

  // Update east room
  if (gs.braz) {
    if (dungeon["east room"].e.east) {
      // Both brazier extinguished AND eastern passage opened
      dungeon["east room"].ld = "a room of finished stone with high arched ceiling and soaring columns that disappear into shadowy heights. The bronze brazier stands at the center, now dark and cold. With the flame extinguished, you can now see intricate star patterns carved deep into the brazier's surface—constellations and celestial symbols that were completely invisible while the fire blazed. The eastern wall has opened to reveal a passage beyond, and ancient magical symbols still glow faintly around the opening.";
    } else {
      // Just brazier extinguished
      dungeon["east room"].ld = "a room of finished stone with high arched ceiling and soaring columns that disappear into shadowy heights. The bronze brazier stands at the center, now dark and cold. With the flame extinguished, you can now see intricate star patterns carved deep into the brazier's surface—constellations and celestial symbols that were completely invisible while the fire blazed.";
    }
  } else if (dungeon["east room"].e.east) {
    // Eastern passage opened but brazier still lit
    dungeon["east room"].ld = "a room of finished stone with high arched ceiling and soaring columns that disappear into shadowy heights. A brilliant brazier stands at the center, its bronze bowl crackling with a small but intense flame that casts dancing shadows on the walls. The eastern wall has opened to reveal a passage beyond, and ancient magical symbols still glow faintly around the opening.";
  }

  // Update center room
  if (gs.trap) {
    dungeon["center room"].ld = "the very heart of the dungeon, a windowless chamber lit only by the eerie light of glowing fungi high above. The fungi pulse with a sickly greenish glow, their surfaces appearing slimy and damp—they look extremely poisonous and give off an acrid smell that makes your eyes water. A thick hemp rope hangs down from a room above, its braided fibers trailing across the stone floor in loose coils. The rope is weathered but sturdy, with large knots tied at regular intervals that would make it easy to climb. Most notably, what was once solid stone floor now reveals an open trapdoor leading down into darkness below.";
  }

  // Update basement
  if (gs.mech) {
    dungeon["basement"].ld = "a stone basement with thick pillars supporting the ceiling above. Ancient mechanisms and bronze gears are built into the walls, now humming with power after being properly activated. The gears click away happily in their clockwork rhythm, and the main mechanism glows with a faint copper light. The heavy stone door to the west stands wide open, revealing a dark passage beyond.";
  }

  // Update crypt
  if (gs.cry) {
    dungeon["crypt"].ld = "an ancient crypt where weathered stone sarcophagi loom like silent sentinels in the perpetual gloom. The air hangs heavy with the weight of centuries, and shadows seem to writhe between the burial chambers. Faded murals depicting celestial events cover the walls, their once-vibrant colors now bleeding into darkness. The reflecting pool that once captured light now lies dormant and dark, its magical energies spent. Where the western wall once stood solid, a passage now opens into deeper darkness.";
  }

  // Update underground lake - CONDITIONAL ON LAKE STATE
  if (gs.lake) {
    // Lake is drained - add down exit
    if (!dungeon["underground lake"].e.down) {
      dungeon["underground lake"].e.down = "lake chamber";
    }
    dungeon["underground lake"].ld = "what was once a vast underground lake now lies empty, its ancient waters having drained away through hidden channels in the depths. The lake bed stretches endlessly into shadow, revealing intricate patterns carved into the stone floor that were hidden beneath the waters for millennia. The island sanctuary remains, now accessible by the fully exposed stone causeway, and upon it the altar stands transformed. The silver statue and spiral hourglass rest permanently fused within their indentations, both glowing with the same ethereal radiance as the altar itself—a testament to the ancient ritual completed here. Ancient stone steps, carved with the same mystical symbols, descend from the altar's base down into a magnificent chamber that was hidden beneath the lake's depths.";
  } else {
    // Lake not drained - ensure no down exit
    delete dungeon["underground lake"].e.down;
  }

  // Update puzzle room - CONDITIONAL ON PUZZLE STATE
  if (gs.puzz) {
    dungeon["puzzle room"].ld = "an ancient chamber with smooth stone walls. In the center lies a large stone platform with a 3x3 grid, now solved and complete. The runes have been arranged in their proper order, and the hidden compartment in the center stands open and empty, having yielded its crystalline treasure. The air no longer shimmers with magic—the puzzle's purpose has been fulfilled.";
  }

  // Update secret room - with portal description
  if (gs.fin) {
    dungeon["secret"].ld = "a secret room, dark, and filled with swirling mist. The ornate chest stands open in the corner, and from within erupts a magnificent portal of pure starlight. The swirling vortex pulses with impossible colors, showing glimpses of otherworldly realms beyond—floating islands, crystal cities, and landscapes that defy imagination. The portal hums with ancient power, offering escape from this dungeon... but to where, you cannot say.";
  }
}

export function calculateProgress(
  dungeon: { [key: string]: Room },
  gs: GameState
): ProgressInfo {
  let m = 0;
  const t = 9;
  if (gs.tel) m++;
  if (gs.mag) m++;
  if (gs.trap) m++;
  if (gs.mech) m++;
  if (gs.cry) m++;
  if (gs.garg) m++;
  if (gs.ciph) m++;
  if (gs.lake) m++;
  if (gs.fin) m++;

  let v = 0;
  const tot = Object.keys(dungeon).length;
  for (const n in dungeon) {
    if (dungeon[n].v) v++;
  }

  return {
    pct: Math.round(((m + v / tot * 3) / (t + 3)) * 100),
    comp: m,
    tot: t,
    vis: v,
    totRooms: tot
  };
}

export async function saveGameToStorage(
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room },
  runes: string[]
): Promise<boolean> {
  try {
    const saveData: SaveData = {
      l: char.loc,
      i: char.inv,
      f: gs,
      runes: runes,
      r: {}
    };

    for (const n in dungeon) {
      const r = dungeon[n];
      saveData.r[n] = {
        v: r.v || false,
        c: r.c.slice(),
        e: JSON.parse(JSON.stringify(r.e))
      };
    }

    const saveString = JSON.stringify(saveData);

    // Save to localStorage (instant, always works)
    localStorage.setItem('dungeonEscapeSave', saveString);

    // Also save to cloud (async, may fail if offline)
    // Don't block on cloud save failure - localStorage is the source of truth
    saveGameToCloud(saveData).catch(error => {
      logger.warn('Cloud save failed, but local save succeeded:', error);
    });

    return true;
  } catch (e: any) {
    logger.error('Save error:', e);
    return false;
  }
}

export async function loadGameFromStorage(
  char: Character,
  gs: GameState,
  dungeon: { [key: string]: Room },
  runes: string[]
): Promise<{ success: boolean; location?: string; source?: 'cloud' | 'local' }> {
  try {
    let saveData: SaveData | null = null;
    let source: 'cloud' | 'local' = 'local';

    // Try to load from cloud first
    try {
      const cloudData = await loadGameFromCloud();
      if (cloudData) {
        saveData = cloudData;
        source = 'cloud';
        logger.log('Loaded save from cloud');
      }
    } catch (cloudError) {
      logger.warn('Failed to load from cloud, falling back to local:', cloudError);
    }

    // Fall back to localStorage if cloud load failed
    if (!saveData) {
      const saveString = localStorage.getItem('dungeonEscapeSave');
      if (!saveString) {
        return { success: false };
      }
      saveData = JSON.parse(saveString);
      logger.log('Loaded save from localStorage');
    }

    // Final null check before using saveData
    if (!saveData) {
      return { success: false };
    }

    char.loc = saveData.l;
    char.inv = saveData.i || [];
    Object.assign(gs, saveData.f);
    runes.splice(0, runes.length, ...(saveData.runes || createInitialRunes()));

    for (const n in saveData.r) {
      if (dungeon[n]) {
        dungeon[n].v = saveData.r[n].v;
        dungeon[n].c = saveData.r[n].c.slice();
        dungeon[n].e = JSON.parse(JSON.stringify(saveData.r[n].e));
      }
    }

    updateRoomDescriptions(dungeon, gs);
    return { success: true, location: char.loc, source };
  } catch (e: any) {
    logger.error('Load error:', e);
    return { success: false };
  }
}

export async function hasSavedGame(): Promise<boolean> {
  // Check localStorage first (fast, synchronous)
  const hasLocalSave = !!localStorage.getItem('dungeonEscapeSave');

  if (hasLocalSave) {
    return true;
  }

  // If no local save, check cloud
  try {
    return await hasCloudSave();
  } catch (error) {
    logger.error('Error checking for cloud save:', error);
    return false;
  }
}